import type { Handedness, PinchCalibration, PinchCalibrationProgress, PinchState } from '../domain/types'

export interface LoopTapInput {
  distanceRatio: number
  confidence: number
  timestampMs: number
}

interface CalibrationOptions {
  holdMs?: number
  transitionDelayMs?: number
  minConfidence?: number
}

export class PinchCalibrator {
  private readonly holdMs: number
  private readonly transitionDelayMs: number
  private readonly minConfidence: number
  private phaseStartedMs = 0
  private samples: number[] = []
  private openMedian = 0
  private value: PinchCalibrationProgress = { phase: 'idle', progress: 0, calibration: null }

  constructor(private readonly handedness: Handedness, options: CalibrationOptions = {}) {
    this.holdMs = options.holdMs ?? 750
    this.transitionDelayMs = options.transitionDelayMs ?? 350
    this.minConfidence = options.minConfidence ?? 0.65
  }

  get progress(): PinchCalibrationProgress {
    return { ...this.value, calibration: this.value.calibration ? { ...this.value.calibration } : null }
  }

  start(timestampMs: number): void {
    this.phaseStartedMs = timestampMs
    this.samples = []
    this.openMedian = 0
    this.value = { phase: 'open', progress: 0, calibration: null }
  }

  cancel(): PinchCalibrationProgress {
    this.phaseStartedMs = 0
    this.samples = []
    this.openMedian = 0
    this.value = { phase: 'idle', progress: 0, calibration: null }
    return this.progress
  }

  push(input: LoopTapInput): PinchCalibrationProgress {
    if (this.value.phase !== 'open' && this.value.phase !== 'closed') return this.progress
    if (input.confidence < this.minConfidence || !Number.isFinite(input.distanceRatio)) return this.cancel()
    const elapsed = Math.max(0, input.timestampMs - this.phaseStartedMs)
    if (this.value.phase === 'closed' && elapsed < this.transitionDelayMs) return this.progress
    this.samples.push(Math.max(0, input.distanceRatio))
    const sampleElapsed = this.value.phase === 'closed' ? elapsed - this.transitionDelayMs : elapsed
    this.value = { ...this.value, progress: clamp01(sampleElapsed / this.holdMs) }
    if (sampleElapsed < this.holdMs || this.samples.length < 4) return this.progress

    if (this.value.phase === 'open') {
      this.openMedian = median(this.samples)
      this.samples = []
      this.phaseStartedMs = input.timestampMs
      this.value = { phase: 'closed', progress: 0, calibration: null }
      return this.progress
    }

    const closedMedian = median(this.samples)
    const gap = this.openMedian - closedMedian
    const valid = gap >= 0.18 && closedMedian <= this.openMedian * 0.72
    const calibration: PinchCalibration = {
      handedness: this.handedness,
      openMedian: this.openMedian,
      closedMedian,
      contactThreshold: closedMedian + gap * 0.3,
      releaseThreshold: closedMedian + gap * 0.7,
      valid,
      calibratedAt: Date.now()
    }
    this.value = { phase: valid ? 'complete' : 'invalid', progress: 1, calibration }
    return this.progress
  }
}

export interface LoopTapOptions {
  calibration?: PinchCalibration | null
  debounceMs?: number
  minConfidence?: number
  stableFrames?: number
  // A pinch released within this window is a tap; kept longer it becomes a hold.
  tapMaxMs?: number
  holdMs?: number
}

// Pinch semantics follow touch conventions: a quick pinch-and-release is a tap
// (loop command), a pinch kept closed is a hold (hush). The tap fires on release
// so a hold never also fires a tap.
export class LoopTapDetector {
  private calibration: PinchCalibration | null
  private readonly debounceMs: number
  private readonly minConfidence: number
  private readonly stableFrames: number
  private readonly tapMaxMs: number
  private readonly holdMs: number
  private armed = false
  private contactFrames = 0
  private releaseFrames = 0
  private contactSinceMs: number | null = null
  private holdFired = false
  private lastTapMs = Number.NEGATIVE_INFINITY

  constructor(options: LoopTapOptions = {}) {
    this.calibration = options.calibration?.valid ? { ...options.calibration } : null
    this.debounceMs = options.debounceMs ?? 220
    this.minConfidence = options.minConfidence ?? 0.65
    this.stableFrames = Math.max(1, options.stableFrames ?? 3)
    this.tapMaxMs = options.tapMaxMs ?? 450
    this.holdMs = options.holdMs ?? 900
    this.armed = false
  }

  get currentCalibration(): PinchCalibration | null {
    return this.calibration ? { ...this.calibration } : null
  }

  setCalibration(calibration: PinchCalibration | null): void {
    this.calibration = calibration?.valid ? { ...calibration } : null
    this.reset()
  }

  push(input: LoopTapInput): PinchState {
    const distanceRatio = Math.max(0, input.distanceRatio)
    if (!this.calibration) return state('uncalibrated', input)
    if (input.confidence < this.minConfidence) {
      this.reset()
      return state('lost', input)
    }

    if (this.contactSinceMs !== null) {
      // In a pinch: wait for release, or cross into a hold.
      const heldMs = input.timestampMs - this.contactSinceMs
      const progress = clamp01(heldMs / this.holdMs)
      if (distanceRatio >= this.calibration.releaseThreshold) {
        const wasHold = this.holdFired
        const tap = !wasHold && heldMs <= this.tapMaxMs && input.timestampMs - this.lastTapMs >= this.debounceMs
        this.contactSinceMs = null
        this.holdFired = false
        this.contactFrames = 0
        this.armed = true
        if (tap) this.lastTapMs = input.timestampMs
        return { ...state(tap ? 'accepted' : 'armed', input), tap }
      }
      if (!this.holdFired && heldMs >= this.holdMs) {
        this.holdFired = true
        return { ...state('held', input), hold: true, holdProgress: 1 }
      }
      return { ...state(this.holdFired ? 'held' : 'contact', input), holdProgress: this.holdFired ? 1 : progress }
    }

    if (!this.armed) {
      this.contactFrames = 0
      if (distanceRatio >= this.calibration.releaseThreshold) {
        this.releaseFrames += 1
        if (this.releaseFrames >= this.stableFrames) {
          this.armed = true
          this.releaseFrames = 0
          return state('armed', input)
        }
        return state('release', input)
      }
      this.releaseFrames = 0
      return state('release', input)
    }

    if (distanceRatio > this.calibration.contactThreshold) {
      this.contactFrames = 0
      return state('armed', input)
    }
    this.contactFrames += 1
    if (this.contactFrames < this.stableFrames) return state('contact', input)
    // Stable contact: the pinch has begun; date it from the first stable frame.
    this.contactSinceMs = input.timestampMs
    this.holdFired = false
    return state('contact', input)
  }

  lost(): PinchState {
    this.reset()
    return { state: this.calibration ? 'lost' : 'uncalibrated', tap: false, hold: false, holdProgress: 0, distanceRatio: 1, confidence: 0 }
  }

  reset(): void {
    this.armed = false
    this.contactFrames = 0
    this.releaseFrames = 0
    this.contactSinceMs = null
    this.holdFired = false
    this.lastTapMs = Number.NEGATIVE_INFINITY
  }
}

function state(name: PinchState['state'], input: LoopTapInput): PinchState {
  return { state: name, tap: false, hold: false, holdProgress: 0, distanceRatio: Math.max(0, input.distanceRatio), confidence: input.confidence }
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b)
  const middle = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 0 ? ((sorted[middle - 1] ?? 0) + (sorted[middle] ?? 0)) / 2 : sorted[middle] ?? 0
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value))
}
